import { useEffect } from 'react';
import { NavLink, useParams, useNavigate } from 'react-router-dom';
import { cn } from '@/lib/cn';
import { useSession } from '@/hooks/useSession';
import { useAppSelector } from '@/store/hooks';
import { selectCurrentGroupId } from '@/store/selectors';
import { ProfileSection } from './ProfileSection';
import { ChangePasswordSection } from './ChangePasswordSection';
import { CompanyInfoSection } from './CompanyInfoSection';
import AccountSettingsWorkspace from '../../app/views/accountSettings/accountSettingsWorkspace/AccountSettingsWorkspace';
import { AccountSettingsBilling } from '../../app/views/accountSettings/accountSettingsBilling/AccountSettingsBilling';
import { AccountSettingsSupport } from '../../app/views/accountSettings/accountSettingsSupport/AccountSettingsSupport';

import {
  getValueFromUserAttributes,
  userIsCurrentRootGroupCreator,
  getCurrentUserCreatorGroup,
} from '../../util/UtilFunctions';

export default function AccountPage() {
  const { id: tab } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const currentUserGroup = useAppSelector(selectCurrentGroupId);
  const currentUserInfo = useAppSelector((state) => state.user.oldUserDetails) as
    Record<string, unknown> | null;

  const {
    groupsList,
    updateIsQuerying,
    updateCurrentUserInfo,
    initiateSession,
    updateGroupsList,
    updateRootWorkspaceDetails,
    rootWorkspaceDetails,
    getWorkspacePeopleList,
    updateCurrentItem,
  } = useSession();

  const history = { push: (path: string) => navigate(path) };

  // Set breadcrumb item on mount / when user info loads
  useEffect(() => {
    if (!currentUserInfo) return;
    const userInfo = currentUserInfo as Record<string, unknown>;
    const cognitoAttrs = (userInfo.cognitoAttributes as Record<string, unknown>)?.UserAttributes;
    if (!cognitoAttrs) return;
    const firstName = getValueFromUserAttributes(cognitoAttrs, 'given_name');
    const lastName = getValueFromUserAttributes(cognitoAttrs, 'family_name');
    updateCurrentItem({
      id: { S: (cognitoAttrs as Record<string, unknown>).Username },
      title: { S: `${firstName} ${lastName}` },
      itemtype: { S: 'account' },
    });
  }, [currentUserInfo]);

  if (!currentUserInfo) return null;

  const hasGroups = groupsList && groupsList.length > 0;
  const isRootCreator = userIsCurrentRootGroupCreator(
    groupsList,
    currentUserInfo,
    currentUserGroup,
  );
  const creatorGroup = getCurrentUserCreatorGroup(currentUserInfo, groupsList);

  return (
    <div className="relative mt-7 w-full bg-card">
      {/* Tab Navigation */}
      <nav className="w-full border-b border-border bg-card">
        <ul className="flex list-none p-0 m-0">
          <TabLink to="/in/account/profile">Profile</TabLink>
          {hasGroups && <TabLink to="/in/account/workspace">Workspaces</TabLink>}
          {isRootCreator && <TabLink to="/in/account/billing">Billing</TabLink>}
          <TabLink to="/in/account/support">Support</TabLink>
        </ul>
      </nav>

      {/* Tab Content */}
      {tab === 'profile' && (
        <div>
          <ProfileSection
            currentUserInfo={currentUserInfo}
            updateCurrentUserInfo={updateCurrentUserInfo}
            updateIsQuerying={updateIsQuerying}
            getWorkspacePeopleList={getWorkspacePeopleList}
          />
          {creatorGroup && (
            <CompanyInfoSection
              currentUserInfo={currentUserInfo}
              groupsList={groupsList}
              rootWorkspaceDetails={rootWorkspaceDetails}
              updateRootWorkspaceDetails={updateRootWorkspaceDetails}
              currentUserCreatorGroup={creatorGroup}
            />
          )}
          <ChangePasswordSection updateIsQuerying={updateIsQuerying} />
        </div>
      )}

      {tab === 'workspace' && hasGroups && (
        <AccountSettingsWorkspace
          groupsList={groupsList}
          currentUserGroup={currentUserGroup}
          currentUserInfo={currentUserInfo}
          getWorkspacePeopleList={getWorkspacePeopleList}
          updateIsQuerying={updateIsQuerying}
          initiateSession={initiateSession}
          refreshList={updateGroupsList}
          updateCurrentUserInfo={updateCurrentUserInfo}
          history={history}
        />
      )}

      {tab === 'billing' && isRootCreator && <AccountSettingsBilling />}
      {tab === 'support' && <AccountSettingsSupport />}
    </div>
  );
}

function TabLink({ to, children }: { to: string; children: React.ReactNode }) {
  return (
    <li>
      <NavLink
        to={to}
        className={({ isActive }) =>
          cn(
            'flex flex-col items-center w-36 no-underline',
            isActive ? 'text-foreground' : 'text-muted-foreground',
          )
        }
      >
        {({ isActive }) => (
          <>
            <span className="py-4 text-sm font-semibold transition-colors hover:text-foreground">
              {children}
            </span>
            <div
              className={cn(
                'h-2.5 w-full rounded-t-lg',
                isActive ? 'bg-foreground' : 'bg-transparent',
              )}
            />
          </>
        )}
      </NavLink>
    </li>
  );
}
